import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import FileSaver from 'file-saver'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import { VbaMacroGenerator } from './VbaMacroGenerator'

import {
  useLazyExportConsultationsQuery,
  useLazyExportPatientsQuery,
  useLazyExportMedicalActsQuery,
  useLazyExportMesuresQuery,
  useLazyExportPregnanciesQuery,
} from '@/core/services/data-export'
import { DataExportParams } from '@/core/api/data-export'
import { Establishment } from '@/core/models/establishment.model'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getEstablishments, selectEstablishments } from '@/redux/reducers/admin.reducer'

type ExportType = 'consultations' | 'patients' | 'medical-acts' | 'mesures' | 'pregnancies'

export function DataExportPage() {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const isSuperAdmin = useLocation().pathname.startsWith('/super-admin')

  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null)
  const [loadingType, setLoadingType] = useState<ExportType | null>(null)

  const dispatch = useAppDispatch()
  const establishments = useAppSelector(selectEstablishments)

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getEstablishments())
    }
  }, [dispatch, isSuperAdmin])

  const [triggerConsultations] = useLazyExportConsultationsQuery()
  const [triggerPatients] = useLazyExportPatientsQuery()
  const [triggerMedicalActs] = useLazyExportMedicalActsQuery()
  const [triggerMesures] = useLazyExportMesuresQuery()
  const [triggerPregnancies] = useLazyExportPregnanciesQuery()

  const triggerMap: Record<ExportType, (params: DataExportParams) => ReturnType<typeof triggerConsultations>> = {
    consultations: triggerConsultations,
    patients: triggerPatients,
    'medical-acts': triggerMedicalActs,
    mesures: triggerMesures,
    pregnancies: triggerPregnancies,
  }

  const handleExport = async (type: ExportType, defaultFileName: string) => {
    if (!startDate || !endDate) {
      enqueueSnackbar(t('dataExport.error.dateRequired', { defaultValue: 'Please select start and end dates' }), { variant: 'warning' })
      return
    }

    setLoadingType(type)
    try {
      enqueueSnackbar(t('dataExport.preparing', { defaultValue: 'Preparing export...' }), { variant: 'info' })

      const params: DataExportParams = { startDate, endDate }
      if (selectedEstablishment) {
        params.establishments = selectedEstablishment._id
      }

      const response = await triggerMap[type](params).unwrap()

      if (!response.csvContent || response.csvContent === 'No data available for the selected filters') {
        enqueueSnackbar(t('dataExport.noData', { defaultValue: 'No data available for the selected filters' }), { variant: 'warning' })
        setLoadingType(null)
        return
      }

      const blob = new Blob([response.csvContent], { type: 'text/csv;charset=utf-8' })
      FileSaver.saveAs(blob, response.fileName || defaultFileName)

      enqueueSnackbar(t('dataExport.success', { defaultValue: 'Export successful' }), { variant: 'success' })
    } catch (error) {
      console.error('Export error:', error)
      enqueueSnackbar(t('dataExport.error.generic', { defaultValue: 'Export failed. Please try again.' }), { variant: 'error' })
    } finally {
      setLoadingType(null)
    }
  }

  const exports: Array<{ type: ExportType; label: string; fileName: string }> = [
    {
      type: 'consultations',
      label: t('dataExport.consultations', { defaultValue: 'Consultations' }),
      fileName: `consultations-${startDate}-${endDate}.csv`,
    },
    {
      type: 'patients',
      label: t('dataExport.patients', { defaultValue: 'Patients' }),
      fileName: `patients-${startDate}-${endDate}.csv`,
    },
    {
      type: 'medical-acts',
      label: t('dataExport.medicalActs', { defaultValue: 'Medical Acts' }),
      fileName: `medical-acts-${startDate}-${endDate}.csv`,
    },
    {
      type: 'mesures',
      label: t('dataExport.mesures', { defaultValue: 'Measures' }),
      fileName: `mesures-${startDate}-${endDate}.csv`,
    },
    {
      type: 'pregnancies',
      label: t('dataExport.pregnancies', { defaultValue: 'Pregnancies' }),
      fileName: `pregnancies-${startDate}-${endDate}.csv`,
    },
  ]

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('dataExport.title', { defaultValue: 'Data Export' })}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('dataExport.description', { defaultValue: 'Export data as CSV files for the selected date range.' })}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={isSuperAdmin ? 4 : 5}>
            <TextField
              type="date"
              label={t('dataExport.startDate', { defaultValue: 'Start Date' })}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={isSuperAdmin ? 4 : 5}>
            <TextField
              type="date"
              label={t('dataExport.endDate', { defaultValue: 'End Date' })}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Autocomplete
                  options={establishments || []}
                  getOptionLabel={(option) => option.name}
                  value={selectedEstablishment}
                  onChange={(_, newValue) => setSelectedEstablishment(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t('common.establishment', { defaultValue: 'Establishment' })} />
                  )}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Stack spacing={2}>
        {exports.map(({ type, label, fileName }) => (
          <Paper key={type} sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">{label}</Typography>
              <Button
                variant="contained"
                startIcon={loadingType === type ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />}
                disabled={loadingType !== null}
                onClick={() => handleExport(type, fileName)}
              >
                {t('dataExport.download', { defaultValue: 'Download CSV' })}
              </Button>
            </Box>
          </Paper>
        ))}
      </Stack>

      <VbaMacroGenerator />
    </Container>
  )
}

export default DataExportPage
